import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion } from 'framer-motion';
import styled, { keyframes, css } from 'styled-components';
import { FiCalendar, FiCheck, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { theme } from '../styles/theme';
import { formatCalendarDate, formatCalendarDateWithLabel, getDateFormatLabel, getUserLocale } from '../utils/dateUtils';

// Pulsing animation for error state
const errorPulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.2);
    border-color: rgba(239, 68, 68, 0.5);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(239, 68, 68, 0.1);
    border-color: rgba(239, 68, 68, 1);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
    border-color: rgba(239, 68, 68, 0.5);
  }
`;

// Styled Components
const DateSelectorContainer = styled(motion.div)`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const DateCard = styled(motion.button)`
  background: ${({ $hasError, $selected, theme }) =>
    $hasError
      ? theme.colors.background.card
      : $selected
        ? theme.colors.background.card
        : theme.colors.background.card};
  border: 2px solid ${({ $hasError, $selected, theme }) =>
    $hasError
      ? theme.colors.border.error
      : $selected
        ? theme.colors.success
        : theme.colors.border.default};
  opacity: 1;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  transition: all ${({ theme }) => theme.transitions.normal};
  position: relative;
  overflow: hidden;
  width: 100%;
  text-align: left;
  box-shadow: ${({ theme }) => theme.shadows.subtle};

  ${({ $hasError }) => $hasError && css`
    animation: ${errorPulse} 2s infinite ease-in-out;
  `}

  &:focus-visible {
    outline: none;
    border-color: ${({ $hasError, theme }) =>
    $hasError
      ? theme.colors.text.error
      : theme.colors.primary};
    box-shadow: ${({ $hasError, theme }) =>
    $hasError
      ? `0 0 0 3px ${theme.colors.border.error}`
      : `0 0 0 3px ${theme.colors.success}`};
  }

  &:hover {
    border-color: ${({ $selected, $hasError, theme }) =>
    $hasError
      ? theme.colors.border.error
      : $selected
        ? theme.colors.success
        : theme.colors.primary};
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const DateIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.gradient.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
`;

const DateDetails = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const DateText = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  margin-bottom: 4px;
`;

const DateSubtext = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const PlaceholderText = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 2px;
`;

const PlaceholderSubtext = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.muted};
  line-height: 1.2;
`;

const Checkmark = styled(motion.div)`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.success};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  flex-shrink: 0;
`;

const EmptyCircle = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.background.card};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  flex-shrink: 0;
`;

// Calendar Popup Components
const CalendarOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85); /* More opaque for better coverage */
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  
  @media (min-width: 768px) {
    padding: 24px;
  }

  @media (max-width: 767px) {
    align-items: flex-end;
    padding: 0;
    background: rgba(15, 23, 42, 1); /* Fully opaque on mobile */
  }
`;

const CalendarContainer = styled(motion.div)`
  background: ${({ theme }) => theme.colors.background.card};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing.lg};
  width: 90%; 
  max-width: 600px; /* Aligned with main app card */
  box-shadow: ${({ theme }) => theme.shadows.card};
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
  margin: 0 auto;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${({ theme }) => theme.colors.gradient.primary};
    border-radius: ${({ theme }) => theme.borderRadius.xl} ${({ theme }) => theme.borderRadius.xl} 0 0;
  }
  
  /* Smooth scrolling */
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  
  @media (min-width: 480px) {
    max-width: 600px;
    padding: ${({ theme }) => theme.spacing.xl};
  }
  
  /* Medium screens (14-inch laptops) - adjust max-height for better fit */
  @media (min-width: 768px) and (max-height: 900px) {
    max-width: 600px;
    padding: ${({ theme }) => theme.spacing.xl};
    max-height: 80vh;
  }
  
  @media (min-width: 768px) {
    max-width: 600px;
    padding: ${({ theme }) => theme.spacing.xl};
  }

  @media (max-width: 767px) {
    width: 100%;
    max-width: 100%;
    max-height: 100vh;
    max-height: 100dvh; /* Use dynamic viewport height for mobile */
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    border-top-left-radius: ${({ theme }) => theme.borderRadius.xl};
    border-top-right-radius: ${({ theme }) => theme.borderRadius.xl};
    margin: 0;
    padding: ${({ theme }) => theme.spacing.md}; /* Reduced padding for mobile */
  }
`;

const CalendarPopupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  position: relative;
  z-index: 1;
  
  @media (min-width: 480px) {
    margin-bottom: 20px;
  }
`;

const CalendarPopupTitle = styled.h3`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  margin: 0;
  text-align: center;
  flex: 1;
  
  @media (min-width: 480px) {
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
  }
`;

const CalendarPopupDescription = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin: 0 0 16px 0;
  line-height: 1.5;
  opacity: 0.8;
  text-align: center;
  position: relative;
  z-index: 1;
  
  @media (min-width: 480px) {
    margin: 0 0 20px 0;
  }
`;

const CalendarFrame = styled.div`
  background: ${({ theme }) => theme.colors.background.card};
  border: 2px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  position: relative;
  overflow: visible;
  flex-shrink: 0;
  
  @media (min-width: 480px) {
    padding: ${({ theme }) => theme.spacing.lg};
    margin-bottom: ${({ theme }) => theme.spacing.lg};
  }
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  gap: ${({ theme }) => theme.spacing.sm};
  
  @media (min-width: 480px) {
    margin-bottom: ${({ theme }) => theme.spacing.lg};
  }
`;

const CalendarTitle = styled.h3`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  margin: 0;
  text-align: center;
  flex: 1;
  white-space: nowrap;
  
  @media (min-width: 480px) {
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
  }
`;

const CalendarNavButton = styled(motion.button)`
  background: ${({ theme }) => theme.colors.background.card};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  color: ${({ theme }) => theme.colors.text.primary};
  width: 36px;
  height: 36px;
  min-width: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  font-weight: bold;
  transition: all ${({ theme }) => theme.transitions.normal};
  position: relative;
  z-index: 1;
  box-shadow: ${({ theme }) => theme.shadows.subtle};
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.primary};
    color: white;
    transform: scale(1.1);
    box-shadow: ${({ theme }) => theme.shadows.prominent};
  }

  &:active {
    transform: scale(0.95);
  }
  
  @media (min-width: 480px) {
    width: 40px;
    height: 40px;
    min-width: 40px;
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px; /* More breathable than 2px but tighter than 8px */
  margin-bottom: ${({ theme }) => theme.spacing.md};
  width: 100%;
  
  @media (min-width: 480px) {
    gap: 8px;
  }
`;

const CalendarDayHeader = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  text-align: center;
  padding: 4px 0; /* Reduced padding */
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.8;
  position: relative;
  z-index: 1;
  min-height: 24px; /* Reduced height */
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  flex: 1;
  
  @media (min-width: 480px) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    padding: 8px 4px;
  }
`;

const CalendarDay = styled(motion.button)`
  background: ${({ $isSelected, $isToday, $isDisabled, theme }) =>
    $isSelected
      ? theme.colors.success
      : $isToday
        ? 'rgba(59, 130, 246, 0.1)'
        : 'transparent'};
  color: ${({ $isSelected, $isDisabled, theme }) =>
    $isDisabled
      ? theme.colors.text.muted
      : $isSelected
        ? 'white'
        : theme.colors.text.primary};
  border: ${({ $isSelected, $isToday, theme }) =>
    $isSelected
      ? `2px solid ${theme.colors.success}`
      : $isToday
        ? `1px solid ${theme.colors.primary}`
        : '2px solid transparent'};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 0; /* Remove internal padding, rely on centering */
  cursor: ${({ $isDisabled }) => $isDisabled ? 'not-allowed' : 'pointer'};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ $isSelected, $isToday }) =>
    $isSelected || $isToday ? '600' : '400'};
  opacity: ${({ $isDisabled }) => $isDisabled ? 0.3 : 1};
  transition: all 0.2s ease;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
  min-height: 36px; /* Restored to better size */
  height: 36px;

  &:hover {
    background: ${({ $isSelected, $isDisabled, theme }) =>
    $isDisabled
      ? 'transparent'
      : $isSelected
        ? theme.colors.success
        : 'rgba(59, 130, 246, 0.2)'};
    border-color: ${({ $isSelected, $isDisabled, theme }) =>
    $isDisabled
      ? 'transparent'
      : $isSelected
        ? theme.colors.success
        : theme.colors.primary};
    transform: ${({ $isDisabled }) => $isDisabled ? 'none' : 'scale(1.05)'};
  }
  
  @media (min-width: 480px) {
    min-height: 38px;
    height: 38px;
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    padding: 8px;
  }
`;

const CalendarDateDisplay = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 12px; /* Slightly reduced padding */
  background: ${({ theme }) => theme.colors.background.card};
  border: 2px solid ${({ $hasDate, theme }) => $hasDate ? theme.colors.success : theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  gap: 10px;
  min-height: 44px; /* Reduced height */
  width: 100%;
  box-sizing: border-box;
  
  @media (min-width: 480px) {
    padding: 12px 16px;
    gap: 12px;
    margin-bottom: ${({ theme }) => theme.spacing.lg};
    min-height: 52px;
  }
`;

const CalendarDateIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $hasDate, theme }) => $hasDate ? theme.colors.success : theme.colors.text.muted};
  font-size: 18px;
  flex-shrink: 0;
  
  @media (min-width: 480px) {
    font-size: 20px;
  }
`;

const CalendarDateText = styled.span`
  color: ${({ $hasDate, theme }) => $hasDate ? theme.colors.text.primary : 'transparent'};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: 400;
  
  @media (min-width: 480px) {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
  }
`;

const CalendarActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: relative;
  z-index: 1;
  width: 100%;
`;

const CalendarButton = styled(motion.button)`
  width: 100%;
  padding: 14px 24px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  ${({ $primary, theme }) => $primary ? `
    background: ${theme.colors.gradient.primary};
    color: white;
    border: none;
    box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
    
    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
      filter: brightness(1.1);
    }
  ` : `
    background: transparent;
    color: ${theme.colors.text.error};
    border: 1px solid rgba(239, 68, 68, 0.4);
    
    &:hover:not(:disabled) {
      background: rgba(239, 68, 68, 0.05);
      border-color: ${theme.colors.text.error};
      transform: translateY(-1px);
    }
  `}
  
  &:active:not(:disabled) {
    transform: translateY(0);
    filter: brightness(0.9);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  @media (min-width: 480px) {
    padding: 14px 24px;
    font-size: ${({ theme }) => theme.typography.fontSize.base};
  }
`;

const DateSelectorComponent = ({
  selectedDate,
  onDateChange,
  currentStep,
  completedSteps,
  externalOpen = false,
  onPopupClose
}) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [tempSelectedDate, setTempSelectedDate] = useState(null);


  const handleOpenCalendar = useCallback(() => {
    setTempSelectedDate(selectedDate);
    if (selectedDate) {
      const [y, m, d] = selectedDate.split('-').map(Number);
      setCurrentMonth(new Date(y, m - 1, d));
    } else {
      setCurrentMonth(new Date());
    }
    setShowCalendar(true);
  }, [selectedDate]);

  // Handle external open request
  useEffect(() => {
    if (externalOpen) {
      handleOpenCalendar();
    }
  }, [externalOpen, handleOpenCalendar]);

  const handleCloseCalendar = () => {
    setShowCalendar(false);
    setTempSelectedDate(null);
    if (onPopupClose) onPopupClose();
  };

  const handleConfirmDate = () => {
    if (tempSelectedDate) {
      onDateChange(tempSelectedDate);
    }
    setShowCalendar(false);
    if (onPopupClose) onPopupClose();
  };

  const handleDateSelect = (dateStr) => {
    setTempSelectedDate(dateStr);
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  // Calendar generation logic
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const days = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();

    const daysArray = [];
    // Add empty slots for days before start of month
    for (let i = 0; i < firstDay; i++) {
      daysArray.push(null);
    }
    // Add days of month
    for (let i = 1; i <= days; i++) {
      daysArray.push(new Date(year, month, i));
    }
    return daysArray;
  };

  const days = getDaysInMonth(currentMonth);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <DateSelectorContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.1 }}
    >
      <DateCard
        $selected={!!selectedDate}
        $hasError={!selectedDate}
        onClick={handleOpenCalendar}
        type="button"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        {selectedDate ? (
          <>
            <DateIcon>
              <FiCalendar size={20} />
            </DateIcon>
            <DateDetails>
              <DateText>
                <span style={{ fontWeight: 'inherit' }}>{formatCalendarDate(selectedDate)}</span>
                <span style={{
                  fontWeight: 400,
                  color: theme.colors.text.secondary,
                  fontSize: '0.9em',
                  marginLeft: '8px'
                }}>
                  {(() => {
                    const { label, pattern } = getDateFormatLabel();
                    return `(${label} • ${pattern})`;
                  })()}
                </span>
              </DateText>
              <DateSubtext>Visa Issue Date</DateSubtext>
            </DateDetails>
            <Checkmark
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <FiCheck size={12} />
            </Checkmark>
          </>
        ) : (
          <>
            <DateIcon>
              <FiCalendar size={20} />
            </DateIcon>
            <DateDetails>
              <PlaceholderText>Select Date</PlaceholderText>
              <PlaceholderSubtext>When was your visa issued?</PlaceholderSubtext>
            </DateDetails>
            <EmptyCircle />
          </>
        )}
      </DateCard>

      {/* Required Field Message */}
      {!selectedDate && (
        <motion.div
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          style={{
            color: theme.colors.text.error,
            fontSize: '14px',
            marginTop: '8px',
            marginBottom: '48px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <div style={{
            width: '4px',
            height: '4px',
            borderRadius: '50%',
            background: theme.colors.text.error
          }} />
          Please select a visa issue date to continue
        </motion.div>
      )}

      {/* Calendar Popup */}
      {showCalendar && ReactDOM.createPortal(
        <CalendarOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleCloseCalendar}
        >
          <CalendarContainer
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <CalendarPopupHeader>
              <CalendarPopupTitle>Select Issue Date</CalendarPopupTitle>
            </CalendarPopupHeader>

            <CalendarPopupDescription>
              Choose the date when your visa was issued
            </CalendarPopupDescription>

            <CalendarFrame>
              <CalendarHeader>
                <CalendarNavButton onClick={handlePrevMonth}>
                  <FiChevronLeft />
                </CalendarNavButton>
                <CalendarTitle>
                  {currentMonth.toLocaleDateString(getUserLocale(), { month: 'long', year: 'numeric' })}
                </CalendarTitle>
                <CalendarNavButton onClick={handleNextMonth}>
                  <FiChevronRight />
                </CalendarNavButton>
              </CalendarHeader>

              <CalendarGrid>
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                  <CalendarDayHeader key={day}>{day}</CalendarDayHeader>
                ))}
                {days.map((date, index) => {
                  if (!date) return <div key={`empty-${index}`} />;

                  const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                  const isSelected = tempSelectedDate === dateStr;
                  const isToday = date.getTime() === today.getTime();
                  const isDisabled = date > today; // Can't select future dates for issue date

                  return (
                    <CalendarDay
                      key={dateStr}
                      onClick={() => !isDisabled && handleDateSelect(dateStr)}
                      $isSelected={isSelected}
                      $isToday={isToday}
                      $isDisabled={isDisabled}
                      disabled={isDisabled}
                    >
                      {date.getDate()}
                    </CalendarDay>
                  );
                })}
              </CalendarGrid>
            </CalendarFrame>

            <CalendarDateDisplay $hasDate={!!tempSelectedDate}>
              <CalendarDateIcon $hasDate={!!tempSelectedDate}>
                {tempSelectedDate ? <FiCheck size={18} color={theme.colors.success} /> : <FiCalendar />}
              </CalendarDateIcon>
              <CalendarDateText $hasDate={!!tempSelectedDate}>
                {tempSelectedDate ? (
                  <>
                    <span style={{ fontWeight: 600 }}>{formatCalendarDate(tempSelectedDate)}</span>
                    <span style={{
                      fontWeight: 400,
                      color: theme.colors.text.secondary,
                      fontSize: '0.9em',
                      marginLeft: '8px'
                    }}>
                      {(() => {
                        const { label, pattern } = getDateFormatLabel();
                        return `(${label} • ${pattern})`;
                      })()}
                    </span>
                  </>
                ) : 'No date selected'}
              </CalendarDateText>
              {tempSelectedDate && (
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    marginLeft: 'auto',
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    background: theme.colors.success,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <FiCheck size={12} />
                </motion.div>
              )}
            </CalendarDateDisplay>

            <CalendarActions>
              <CalendarButton
                $primary
                onClick={handleConfirmDate}
                disabled={!tempSelectedDate}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Confirm
              </CalendarButton>
              <CalendarButton
                onClick={handleCloseCalendar}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Cancel
              </CalendarButton>
            </CalendarActions>
          </CalendarContainer>
        </CalendarOverlay>,
        document.body
      )}
    </DateSelectorContainer>
  );
};

// Export memoized version to prevent unnecessary re-renders
export const DateSelector = React.memo(DateSelectorComponent);
